import {GridOptions} from 'ag-grid-community';
import {ColumnOrGroupSpec, GridConfig, GridModel} from '@xh/hoist/cmp/grid';
import {HoistModel, lookup, managed, XH} from '@xh/hoist/core';
import {View} from '@xh/hoist/data';
import {DashViewModel} from '@xh/hoist/desktop/cmp/dash';
import {Icon} from '@xh/hoist/icon';
import {action, bindable, observable} from '@xh/hoist/mobx';
import {wait} from '@xh/hoist/promise';
import {capitalize} from 'lodash';
import {ConfigurableGridModel} from '../../../../cmp/gridSpec/ConfigurableGridModel';
import {positionsCatalog, positionsStoreFields} from '../positions/PositionsGridSpec';
import {moversBaseColumn, moversCapabilities, moversDefaultSpec} from './MoversGridSpec';

type QudrantType = 'gainers_long' | 'gainers_short' | 'losers_long' | 'losers_short';

/**
 * Today's biggest movers in four quadrants - gainers and losers, long and short. One GridSpec sets the columns of all
 * four grids. The quadrants keep their own filter and sort.
 */
export class MoversWidgetModel extends HoistModel {
    @lookup(DashViewModel) dashViewModel: DashViewModel;
    @managed view: View;
    @managed configurableGridModel: ConfigurableGridModel;
    @observable accessor sortColId: string = 'priceChangePct';
    @bindable accessor count = -1;
    @bindable accessor showShort = true;

    gridGainersLong: GridModel;
    gridGainersShort: GridModel;
    gridLosersLong: GridModel;
    gridLosersShort: GridModel;

    private get allGrids(): GridModel[] {
        return [this.gridGainersLong, this.gridGainersShort, this.gridLosersLong, this.gridLosersShort];
    }

    override onLinked() {
        const {dashViewModel} = this;

        this.markPersist('count', {dashViewModel});
        this.markPersist('showShort', {dashViewModel});
        this.markPersist('sortColId', {dashViewModel});

        dashViewModel.extraMenuItems.push({
            icon: Icon.gear(),
            text: 'Configure',
            actionFn: () => this.configurableGridModel.openConfigurator()
        });

        // The first quadrant is the configurable grid's own, the other three are linked to its spec.
        this.configurableGridModel = new ConfigurableGridModel({
            title: 'Movers',
            catalog: positionsCatalog,
            capabilities: moversCapabilities,
            defaultSpec: moversDefaultSpec,
            initialSpec: dashViewModel.viewState?.gridSpec,
            persistCallback: gridSpec => dashViewModel.setViewStateKey('gridSpec', gridSpec),
            baseColumn: moversBaseColumn,
            ...this.quadrantConfig('gainers_long', 'Gainers - Long')
        });
        this.gridGainersLong = this.configurableGridModel.gridModel;
        this.gridGainersShort = this.configurableGridModel.createLinkedGrid(this.quadrantConfig('gainers_short', 'Gainers - Short'));
        this.gridLosersLong = this.configurableGridModel.createLinkedGrid(this.quadrantConfig('losers_long', 'Losers - Long'));
        this.gridLosersShort = this.configurableGridModel.createLinkedGrid(this.quadrantConfig('losers_short', 'Losers - Short'));

        this.view = XH.livePositionsService.cube.createView({
            query: {
                includeLeaves: true,
                dimensions: ['ticker']
            },
            stores: this.allGrids.map(grid => grid.store),
            connect: true
        });

        wait().then(async () => {
            await Promise.all(this.allGrids.map(it => it.whenReadyAsync()));
            this.setSort(this.sortColId);
            this.addReaction({
                track: () => this.count,
                run: () => this.updatePageSize()
            });
        });
    }

    @action
    setSort(colId: string) {
        this.sortColId = colId;
        this.gridGainersLong.setSortBy({colId, sort: 'desc'});
        this.gridGainersShort.setSortBy({colId, sort: 'desc'});
        this.gridLosersLong.setSortBy({colId, sort: 'asc'});
        this.gridLosersShort.setSortBy({colId, sort: 'asc'});
    }

    get agOptions(): GridOptions {
        return {
            ...this.configurableGridModel.agOptions,
            pagination: true,
            suppressPaginationPanel: true,
            paginationPageSize: this.count === -1 ? 0 : this.count,
            paginationAutoPageSize: this.count === -1
        };
    }

    private updatePageSize() {
        if (this.count === -1) {
            this.allGrids.forEach(grid => {
                if (!grid.isReady) return;
                grid.agApi.setGridOption('paginationAutoPageSize', true);
            });
        } else {
            this.allGrids.forEach(grid => {
                if (!grid.isReady) return;
                grid.agApi.setGridOption('paginationAutoPageSize', false);
                grid.agApi.setGridOption('paginationPageSize', this.count);
            });
        }
    }

    /** A quadrant's own store filter, and its header group wrapped around the spec's columns. */
    private quadrantConfig(type: QudrantType, header: string) {
        const gridConfig: Omit<GridConfig, 'columns' | 'sortBy' | 'groupBy'> = {
            store: {
                fields: positionsStoreFields,
                projectionOnly: true,
                filter: {
                    op: 'AND',
                    filters: [
                        {field: 'marketValue', op: type.includes('long') ? '>=' : '<', value: 0},
                        {field: 'pnlDly', op: type.includes('gainers') ? '>' : '<', value: 0}
                    ]
                }
            },
            contextMenu: null,
            colDefaults: {
                movable: false,
                resizable: false
            }
        };
        const wrapColumns = (columns: ColumnOrGroupSpec[]): ColumnOrGroupSpec[] => [
            {
                groupId: type,
                headerName: capitalize(header),
                headerAlign: 'center',
                headerClass: type.includes('gainers') ? 'movers-group movers-group--gainers' : 'movers-group movers-group--losers',
                borders: false,
                children: columns
            }
        ];
        return {gridConfig, wrapColumns};
    }
}
